import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import {produce} from 'immer';
import React, {useCallback, useMemo, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  OnConnect,
  NodeChange,
  applyNodeChanges,
  EdgeChange,
  applyEdgeChanges,
  Edge,
  useReactFlow,
} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import * as metrics from 'metrics/Metrics';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  HandlesContext,
  HandlesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {UncontrolledAutoLayoutButton} from 'ui-library/reactflow/controls/AutoLayoutControlButton';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import buildInteractiveFlowchartFromStaticData from './buildInteractiveFlowchartFromStaticData';
import {getEdgeTypeFromHandleId} from './flowchartIdentifierUtils';
import {
  DEFAULT_FLOWCHART_PROPS,
  READ_ONLY_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES,
  FlowchartStaticEdgeData,
  FlowchartEdge,
  CustomMappingData,
  TransitionSource,
  INTERACTION_FIELDS,
  ProcessFlowchartNodeType,
  StaticTemplateFlowchart,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorLeftSidepanel, {
  LEFT_SIDEPANEL_WIDTH,
} from './ProcessFlowchartConfigEditorLeftSidepanel';
import {convertInteractiveFlowchartToSpecFlowchart} from './processFlowchartConfigEditorNetworkUtils';
import {
  TaskSchemaNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  StaticFlowchartNodeData,
  FlowchartNode,
  isStaticTaskSchemaNodeData,
  isInteractiveTaskNode,
} from './processFlowchartConfigEditorNodeTypes';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE,
} from './processFlowchartConfigEditorRegistry';
import ProcessFlowchartConfigEditorRightSidepanel from './ProcessFlowchartConfigEditorRightSidepanel';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';

interface ProcessFlowchartConfigEditorContentProps {
  flowchart: StaticTemplateFlowchart;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  allSchemasById: SchemaById;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  onChangeCustomMappingData: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
  onChangeStaticFlowchartData: (newStaticFlowchartData: {
    nodesStaticData: Array<StaticFlowchartNodeData>;
    edgesStaticData: Array<FlowchartStaticEdgeData>;
  }) => void;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  onChangeTransitionSource: (edgeId: string, newTransitionSource: TransitionSource) => void;
  refetchFlowchartConfig?: () => void;
  onSaveNow: (newStaticFlowchartData: StaticTemplateFlowchart) => void;
  locked: boolean;
}

function getAllowedNodeTypes(
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator
): Array<ProcessFlowchartNodeType> {
  const allowedTaskNodeTypes = compact(
    flowchartEditorConfigurator.allowedNodeExecutionTypes.map(
      (executionType) => PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE[executionType]
    )
  );
  const allowedNodeTypesForPlatformFeatures = compact(
    Object.entries(PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE)
      .filter(([feature]) =>
        flowchartEditorConfigurator.hasFeature(feature as ProcessFlowchartPlatformFeatureType)
      )
      .map(([_, nodeType]) => nodeType)
  );
  return [...allowedTaskNodeTypes, ...allowedNodeTypesForPlatformFeatures];
}

function selectSingleFieldInNodes(
  nodes: Array<FlowchartNode>,
  selectedNodeId: string | null,
  selectedFieldId: string | null
): Array<FlowchartNode> | null {
  let haveNodesChanged = false;
  const newNodes = produce(nodes, (draftNodes) => {
    for (const node of draftNodes) {
      if (!isStaticTaskSchemaNodeData(node.data)) {
        continue;
      }
      for (const section of node.data.sections) {
        for (const row of section.sectionRows) {
          if (node.id === selectedNodeId && row.id === selectedFieldId) {
            if (!row.isSelected) {
              row.isSelected = true;
              haveNodesChanged = true;
            }
          } else if (row.isSelected) {
            row.isSelected = false;
            haveNodesChanged = true;
          }
        }
      }
    }
  });
  if (haveNodesChanged) {
    return newNodes;
  }
  return null;
}

/**
 * Horizontal flowchart config editor content that displays the specified flowchart config version.
 *
 * Converts static nodes and edges to interactive nodes and edges.
 */
export default function ProcessFlowchartConfigEditorContent(
  props: ProcessFlowchartConfigEditorContentProps
): JSX.Element {
  const interactionProps = props.locked ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;

  const allowedNodeTypes = useMemo(
    () => getAllowedNodeTypes(props.flowchartEditorConfigurator),
    [props.flowchartEditorConfigurator]
  );

  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<StaticFlowchartNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [performInitialAutolayout, setPerformInitialAutolayout] = useState(false);

  // Left Sidepanel state
  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(false);

  // Right Sidepanel state
  const [isRightSidepanelOpen, setIsRightSidepanelOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const openSidepanelForNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedFieldId(null);
    setIsRightSidepanelOpen(true);
  }, []);

  useEffect(() => {
    const newNodes = selectSingleFieldInNodes(nodes, selectedNodeId, selectedFieldId);
    if (newNodes != null) {
      setNodes(newNodes);
    }
  }, [selectedFieldId, selectedNodeId, nodes, setNodes]);

  const openSidepanelForNodeWithField = useCallback((nodeId: string, fieldId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedFieldId(fieldId);
    setIsRightSidepanelOpen(true);
  }, []);

  const handleRightSidepanelClose = useCallback(() => {
    setIsRightSidepanelOpen(false);
    setSelectedNodeId(null);
    setSelectedFieldId(null);
  }, []);

  const {initialNodes, initialEdges} = useMemo(() => {
    const interactiveFlowchart = buildInteractiveFlowchartFromStaticData({
      staticNodesData: props.flowchart.nodesStaticData,
      staticEdgesData: props.flowchart.edgesStaticData,
      options: {
        onNodeHeaderClick: openSidepanelForNode,
        onFieldClick: openSidepanelForNodeWithField,
      },
    });

    return {
      initialNodes: interactiveFlowchart.nodes,
      initialEdges: interactiveFlowchart.edges,
    };
  }, [
    props.flowchart.nodesStaticData,
    props.flowchart.edgesStaticData,
    openSidepanelForNode,
    openSidepanelForNodeWithField,
  ]);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setPerformInitialAutolayout(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!performInitialAutolayout) {
      return;
    }

    // Defer autolayout to the next tick to ensure that node dimensions have rendered and have dimensions.
    // If the nodes do not have dimensions, the layout algorithm falls back to nodes with no dimensions
    // which leads to an unreadable flowchart.
    setTimeout(() => {
      const innerNodes = reactFlowInstance.getNodes();
      const innerEdges = reactFlowInstance.getEdges();
      const flowchartHasNodesWithDimensions =
        innerNodes.length > 0 &&
        innerNodes.some(
          (node) => node.width != null && node.height != null && node.width > 0 && node.height > 0
        );

      const customLayoutSettings: Partial<LayoutSettings<FlowchartNode, Edge>> = {};
      if (!flowchartHasNodesWithDimensions) {
        // This is an unexpected state. If the nodes do not have computed dimensions, we use some defaults
        // which should produce a readable flowchart in most cases.
        // Since we do not have a good logging solution on the frontend we emit a metric to datadog to track
        // this case.
        metrics.count('benchling.processes.autolayout_flowchart_without_dimensions.count', 1);
        // This is a default height that we assume will produce a readable flowchart in most cases.
        // This may not result in the best results for flowcharts with lots of parallel branches and lots of
        // fields in each node.
        const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
        customLayoutSettings.calculateNodeDimensions = (node) => ({
          width: COMMON_NODE_WIDTH,
          height: fallbackNodeHeight,
        });
      }
      const {nodes: layoutNodes, edges: layoutEdges} = layoutFlowchart(
        {
          nodes: innerNodes,
          edges: innerEdges,
        },
        customLayoutSettings
      );

      setNodes(layoutNodes);
      setEdges(layoutEdges);
      setPerformInitialAutolayout(false);
      // After the initial autolayout we need to call fitView to ensure that the flowchart is visible.
      // We defer this to the tick after the autolayout to ensure that the autolayout has been applied.
      setTimeout(() => {
        reactFlowInstance.fitView(DEFAULT_FIT_VIEW_OPTIONS);
      });
    });
  }, [performInitialAutolayout, setNodes, setEdges, reactFlowInstance]);

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      handlesInUse: edges.flatMap((edge) => {
        if (!edge.sourceHandle || !edge.targetHandle) {
          throw new Error('Invalid edge: missing sourceHandle or targetHandle');
        }
        return [edge.sourceHandle, edge.targetHandle];
      }),
    }),
    [edges]
  );

  // Utility function to extract only static data fields from node data
  const extractStaticData = useCallback((nodeData: StaticFlowchartNodeData) => {
    // Extract static data fields from the node data
    const staticData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(nodeData)) {
      if (!INTERACTION_FIELDS.includes(key)) {
        staticData[key] = value;
      }
    }

    return staticData as unknown as StaticFlowchartNodeData;
  }, []);

  const onConnect: OnConnect = useCallback(
    (params) => {
      if (!isNewEdgeValidForFlowchart(params, edges)) {
        return;
      }

      const newEdge = {
        id: uuidv4(),
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: getEdgeTypeFromHandleId(params.sourceHandle!), // sourceHandle is guaranteed to be non-null by isNewEdgeValid
      };

      const updatedEdges = [...edges, newEdge];
      setEdges(updatedEdges);
      // New edges don't trigger onEdgesChange
      props.onChangeStaticFlowchartData({
        nodesStaticData: nodes.map((node) => extractStaticData(node.data)),
        edgesStaticData: updatedEdges as Array<FlowchartStaticEdgeData>,
      });
    },
    [edges, extractStaticData, nodes, props, setEdges]
  );

  const onNodesChangeWithUpdate = useCallback(
    (changes: Array<NodeChange>) => {
      const updatedNodes = applyNodeChanges(changes, nodes);

      // Only update static flowchart data if the static data has actually changed
      const hasDataChanges = updatedNodes.some((updatedNode) => {
        const originalNode = nodes.find((node) => node.id === updatedNode.id);
        if (!originalNode) {
          return true; // New node
        }

        const originalStaticData = extractStaticData(originalNode.data);
        const updatedStaticData = extractStaticData(updatedNode.data);
        return !isEqual(originalStaticData, updatedStaticData);
      });

      if (hasDataChanges) {
        // This should be the only place we update the static flowchart when nodes change,
        // because all node changes will route through here.
        props.onChangeStaticFlowchartData({
          nodesStaticData: updatedNodes.map((node) => extractStaticData(node.data)),
          edgesStaticData: edges as Array<FlowchartStaticEdgeData>,
        });
      }

      onNodesChange(changes);
    },
    [onNodesChange, props, nodes, edges, extractStaticData]
  );

  const onEdgesChangeWithUpdate = useCallback(
    (changes: Array<EdgeChange>) => {
      const updatedEdges = applyEdgeChanges(changes, edges);

      // Only update static flowchart data if edges were added or removed
      const currentEdgeIds = new Set(edges.map((edge) => edge.id));
      const updatedEdgeIds = new Set(updatedEdges.map((edge) => edge.id));

      // Check if any edges were added or removed
      const hasStructuralChanges =
        currentEdgeIds.size !== updatedEdgeIds.size ||
        [...currentEdgeIds].some((id) => !updatedEdgeIds.has(id)) ||
        [...updatedEdgeIds].some((id) => !currentEdgeIds.has(id));

      if (hasStructuralChanges) {
        // This should be the only place we update the static flowchart when edges change,
        // because all edge changes will route through here.
        // EXCEPT, new edges will not route through here.
        props.onChangeStaticFlowchartData({
          nodesStaticData: nodes.map((node) => extractStaticData(node.data)),
          edgesStaticData: updatedEdges as Array<FlowchartStaticEdgeData>,
        });
      }
      onEdgesChange(changes);
    },
    [onEdgesChange, props, nodes, edges, extractStaticData]
  );

  const dragOverHandler = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const addNodeToCanvas = useCallback(
    (newNode: TaskSchemaNode): void => {
      setNodes((currentNodes) => {
        const newNodes = currentNodes.concat(newNode);
        return newNodes;
      });
    },
    [setNodes]
  );

  const taskDefinitionVersionBasedFlowchart = useMemo(
    () => convertInteractiveFlowchartToSpecFlowchart({nodes, edges: edges as Array<FlowchartEdge>}),
    [nodes, edges]
  );

  // Memoize the selected node to prevent unnecessary recalculations
  const selectedNode = useMemo(
    () => (selectedNodeId ? (nodes.find((node) => node.id === selectedNodeId) ?? null) : null),
    [selectedNodeId, nodes]
  );

  const updateTaskNodeInFlowchart = useCallback(
    (nodeId: string, newTaskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema) =>
      produce(nodes, (draftNodes) => {
        const nodeToUpdate = draftNodes.find((node) => node.id === nodeId);
        if (nodeToUpdate == null) {
          return;
        }
        assert(isInteractiveTaskNode(nodeToUpdate), 'Expected node to be an interactive task node');
        const adapter = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(
          nodeToUpdate.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
        );
        nodeToUpdate.data.taskDefinitionVersionSpec = newTaskDefinitionVersionSpec;
        nodeToUpdate.data.nodeName = adapter.getNodeName(newTaskDefinitionVersionSpec);
        nodeToUpdate.data.sections = adapter.createSections(
          newTaskDefinitionVersionSpec,
          nodeToUpdate.data.nodeDetailsType
        );
      }).map((node) => extractStaticData(node.data)),
    [extractStaticData, nodes]
  );

  /**
   * Enables updates to a node from the right sidepanel to be saved immediately to the DB
   * without waiting for an auto save.
   *
   * If isBlocking is true, a network call will be made immediately to save the flowchart config.
   * If isBlocking is false, the node changes will be accumulated with other changes to wait for auto save.
   */
  const onUpdateTaskDefinitionVersion = useCallback(
    (newTaskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema, isBlocking: boolean) => {
      assert(selectedNodeId != null, 'Expected selectedNodeId to be non-null');
      const updatedNodes = updateTaskNodeInFlowchart(selectedNodeId, newTaskDefinitionVersionSpec);

      if (isBlocking) {
        props.onSaveNow({
          nodesStaticData: updatedNodes,
          edgesStaticData: edges as Array<FlowchartStaticEdgeData>,
        });
      } else {
        props.onChangeStaticFlowchartData({
          nodesStaticData: updatedNodes,
          edgesStaticData: edges as Array<FlowchartStaticEdgeData>,
        });
      }
    },
    [props, edges, selectedNodeId, updateTaskNodeInFlowchart]
  );

  return (
    <div className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContentWrapper)}>
      <ReactFlowWrapper fullWidth={true}>
        <div className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContent)}>
          <ProcessFlowchartConfigEditorLeftSidepanel
            isOpen={isLeftSidepanelOpen}
            setIsOpen={setIsLeftSidepanelOpen}
            taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
            allowedNodeTypes={allowedNodeTypes}
            addNodeToCanvas={addNodeToCanvas}
            options={{
              onNodeHeaderClick: openSidepanelForNode,
              onFieldClick: openSidepanelForNodeWithField,
            }}
            rootTaskSchema={props.rootTaskSchema}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
          />
          <div
            style={{
              flexGrow: 1,
              marginLeft: FLOWCHART_SIDE_PANEL_WIDTH,
              position: 'relative',
            }}
          >
            <HandlesContext.Provider value={handlesContext}>
              <ReactFlow
                {...DEFAULT_FLOWCHART_PROPS}
                {...interactionProps}
                nodes={nodes}
                nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
                edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
                edges={edges}
                onNodesChange={onNodesChangeWithUpdate}
                onEdgesChange={onEdgesChangeWithUpdate}
                onConnect={onConnect}
                onDragOver={dragOverHandler}
              >
                <HorizontalControls
                  shiftLeft={isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0}
                  additionalControls={<UncontrolledAutoLayoutButton />}
                />
                <DefaultBackground />
              </ReactFlow>
            </HandlesContext.Provider>
          </div>
          <ProcessFlowchartConfigEditorRightSidepanel
            selectedFieldId={selectedFieldId}
            onChangeSelectedFieldId={setSelectedFieldId}
            interactiveFlowchart={{nodes, edges: edges as Array<FlowchartEdge>}}
            flowchart={taskDefinitionVersionBasedFlowchart}
            selectedNode={selectedNode}
            isOpen={isRightSidepanelOpen}
            onClose={handleRightSidepanelClose}
            onUpdateTaskDefinitionVersion={onUpdateTaskDefinitionVersion}
            allSchemasById={props.allSchemasById}
            flowchartTaskSchema={props.rootTaskSchema}
            customMappingDataByEdgeId={props.customMappingDataByEdgeId}
            onChangeCustomMappingData={props.onChangeCustomMappingData}
            transitionSourceByNodeIdPair={props.transitionSourceByNodeIdPair}
            onChangeTransitionSource={props.onChangeTransitionSource}
            onChangeFlowchart={({nodeChanges, edgeChanges}) => {
              onNodesChangeWithUpdate(nodeChanges);
              onEdgesChangeWithUpdate(edgeChanges);
            }}
            refetchFlowchartConfig={props.refetchFlowchartConfig}
            platformFlowchartFeatures={props.flowchartEditorConfigurator.platformFlowchartFeatures}
            locked={props.locked}
          />
        </div>
      </ReactFlowWrapper>
    </div>
  );
}
